import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, ChevronLeft, FileText, Pencil, Save, X, Trash2, Plus } from "lucide-react";
import { api, API, openAuthedBlob } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function InvoicePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seller, setSeller] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${id}`).then(({ data }) => setInv(data)).catch(() => {
      toast.error("Invoice not found");
      navigate("/history");
    });
  }, [id, navigate]);

  useEffect(() => {
    // Pull the current seller profile so the on-screen preview mirrors the PDF
    // (logo + enabled + position are stored only on the settings doc).
    api.get("/settings/seller").then(({ data }) => setSeller(data)).catch(() => {});
  }, []);

  if (!inv) return <div className="p-8 text-zinc-500">Loading invoice…</div>;

  const intra = inv.intra_state;
  const active = editMode ? draft : inv;
  const items = active.line_items || [];

  const startEdit = () => {
    setDraft({
      ...inv,
      line_items: (inv.line_items || []).map((i) => ({ ...i })),
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditMode(false);
  };

  const updateItem = (idx, key, value) => {
    setDraft((d) => {
      const li = d.line_items.map((x, i) => (i === idx ? { ...x, [key]: value } : x));
      return { ...d, line_items: li };
    });
  };

  const removeItem = (idx) => {
    setDraft((d) => ({
      ...d,
      line_items: d.line_items.filter((_, i) => i !== idx),
    }));
  };

  const addItem = () => {
    setDraft((d) => {
      const lastGst = d.line_items.length ? Number(d.line_items[d.line_items.length - 1].gst_rate) || 18 : 18;
      return {
        ...d,
        line_items: [
          ...d.line_items,
          { description: "", hsn: "", quantity: 1, unit: "Pcs", unit_price: 0, gst_rate: lastGst },
        ],
      };
    });
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const payload = {
        line_items: draft.line_items.map((i) => ({
          ...i,
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          gst_rate: Number(i.gst_rate) || 0,
        })),
      };
      const { data } = await api.put(`/invoices/${id}`, payload);
      setInv(data);
      setEditMode(false);
      setDraft(null);
      toast.success("Invoice updated");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const previewSubtotal = editMode
    ? draft.line_items.reduce(
        (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
        0,
      )
    : inv.subtotal;

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Top bar */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="px-6 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/history")} data-testid="back-history">
            <ChevronLeft size={16} /> Back to History
          </Button>
          <div className="text-sm mono text-zinc-500" data-testid="invoice-header-number">
            {inv.invoice_number} · {inv.invoice_date}
          </div>
          <div className="flex gap-2">
            {inv.po_file_id && (
              <Button variant="outline" size="sm" asChild data-testid="view-po-btn">
                <a href={`${API}/po/${inv.po_file_id}/file`} target="_blank" rel="noreferrer">
                  <FileText size={14} className="mr-1" /> View PO
                </a>
              </Button>
            )}
            {editMode ? (
              <>
                <Button size="sm" variant="outline" onClick={cancelEdit} data-testid="cancel-edit-btn">
                  <X size={14} className="mr-1" /> Cancel
                </Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={saveEdits} disabled={saving} data-testid="save-edit-btn">
                  <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save Changes"}
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={startEdit} data-testid="edit-invoice-btn">
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  size="sm"
                  onClick={async () => {
                    try {
                      await openAuthedBlob(`/invoices/${id}/pdf?v=${inv.updated_at || inv.created_at || ""}`);
                    } catch (err) {
                      toast.error("Failed to download PDF");
                    }
                  }}
                  data-testid="confirm-download-btn"
                >
                  <Download size={14} className="mr-1" /> Confirm &amp; Download
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* A4 Sheet */}
      <div className="py-8">
        <div className="a4-sheet text-[10.5px] leading-snug text-black" data-testid="invoice-sheet">
          {seller?.logo_enabled && seller?.logo_data_url && (
            <div
              className={`mb-3 flex ${
                seller.logo_position === "top-center"
                  ? "justify-center"
                  : seller.logo_position === "top-right"
                  ? "justify-end"
                  : "justify-start"
              }`}
              data-testid="invoice-logo"
            >
              <img
                src={seller.logo_data_url}
                alt="Company logo"
                className="h-[90px] max-w-[50%] object-contain"
                data-testid="invoice-logo-img"
              />
            </div>
          )}
          <div className="text-center border-b-2 border-black pb-2 mb-3">
            <div className="text-lg font-bold tracking-wider">TAX INVOICE</div>
          </div>

          <div className="grid grid-cols-5 border border-black mb-3">
            <div className="col-span-3 p-3 border-r border-black">
              <div className="font-bold text-sm">{inv.seller.name}</div>
              <div className="text-[10px] mt-1 whitespace-pre-line">{inv.seller.address}</div>
              <div className="text-[10px] mt-1">
                <div>GSTIN: <span className="mono">{inv.seller.gstin}</span></div>
                <div>State: {inv.seller.state} ({inv.seller.state_code})</div>
                {inv.seller.pan && <div>PAN: <span className="mono">{inv.seller.pan}</span></div>}
              </div>
            </div>
            <div className="col-span-2 p-3 text-[10px] space-y-1">
              <div><span className="text-zinc-500">Invoice No:</span> <span className="mono font-semibold">{inv.invoice_number}</span></div>
              <div><span className="text-zinc-500">Invoice Date:</span> {inv.invoice_date}</div>
              <div><span className="text-zinc-500">PO No:</span> <span className="mono">{inv.po_number}</span></div>
              <div><span className="text-zinc-500">PO Date:</span> {inv.po_date}</div>
              <div><span className="text-zinc-500">Place of Supply:</span> {inv.place_of_supply}</div>
              <div><span className="text-zinc-500">Category:</span> {intra ? "Intra-State (CGST+SGST)" : "Inter-State (IGST)"}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 border border-black mb-3">
            <div className="p-3 border-r border-black">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Bill To</div>
              <div className="font-semibold">{inv.buyer.name}</div>
              <div className="text-[10px] mt-1 whitespace-pre-line">{inv.buyer.address}</div>
              <div className="text-[10px] mt-1">GSTIN: <span className="mono">{inv.buyer.gstin}</span></div>
              <div className="text-[10px]">State: {inv.buyer.state} ({inv.buyer.state_code})</div>
            </div>
            <div className="p-3">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Ship To</div>
              <div className="font-semibold">{inv.ship_to.name}</div>
              <div className="text-[10px] mt-1 whitespace-pre-line">{inv.ship_to.address}</div>
              <div className="text-[10px] mt-1">GSTIN: <span className="mono">{inv.ship_to.gstin}</span></div>
              <div className="text-[10px]">State: {inv.ship_to.state} ({inv.ship_to.state_code})</div>
            </div>
          </div>

          <table className="w-full border border-black text-[10px] table-fixed" data-testid="invoice-items-table">
            <thead className="bg-zinc-100">
              <tr>
                <th className="border border-black p-1 text-left w-8">#</th>
                <th className="border border-black p-1 text-left">Description</th>
                <th className="border border-black p-1 w-24">HSN</th>
                <th className="border border-black p-1 w-20">Qty</th>
                <th className="border border-black p-1 w-28">Rate</th>
                <th className="border border-black p-1 w-28">Amount</th>
                {intra ? (
                  <>
                    <th className="border border-black p-1 w-24">CGST</th>
                    <th className="border border-black p-1 w-24">SGST</th>
                  </>
                ) : (
                  <th className="border border-black p-1 w-24">IGST</th>
                )}
                <th className="border border-black p-1 w-24">Total</th>
                {editMode && <th className="border border-black p-1 w-8"></th>}
              </tr>
            </thead>
            <tbody className="mono tabular">
              {items.map((it, i) => {
                const qty = Number(it.quantity) || 0;
                const rate = Number(it.unit_price) || 0;
                const gst = Number(it.gst_rate) || 0;
                const taxable = editMode ? qty * rate : Number(it.taxable_amount) || 0;
                const igstAmt = editMode ? (taxable * gst) / 100 : Number(it.igst_amount) || 0;
                const cgstAmt = editMode ? (taxable * gst) / 200 : Number(it.cgst_amount) || 0;
                const sgstAmt = editMode ? (taxable * gst) / 200 : Number(it.sgst_amount) || 0;
                const total = editMode ? taxable + igstAmt + cgstAmt + sgstAmt : Number(it.total) || 0;
                return (
                  <tr key={i} data-testid={`preview-row-${i}`}>
                    <td className="border border-black p-1 text-center align-top">{i + 1}</td>
                    <td className="border border-black p-1 font-sans align-top break-words whitespace-normal">
                      {editMode ? (
                        <Textarea
                          value={it.description}
                          onChange={(e) => updateItem(i, "description", e.target.value)}
                          className="text-xs px-2 py-1 min-h-[36px] leading-snug"
                          data-testid={`edit-desc-${i}`}
                        />
                      ) : (
                        it.description
                      )}
                    </td>
                    <td className="border border-black p-1 text-center align-top break-words">
                      {editMode ? (
                        <Input
                          value={it.hsn}
                          onChange={(e) => updateItem(i, "hsn", e.target.value)}
                          className="h-7 text-xs mono px-1"
                          data-testid={`edit-hsn-${i}`}
                        />
                      ) : (
                        it.hsn
                      )}
                    </td>
                    <td className="border border-black p-1 text-right align-top">
                      {editMode ? (
                        <Input
                          type="number"
                          value={it.quantity}
                          onChange={(e) => updateItem(i, "quantity", e.target.value)}
                          className="h-7 text-xs mono text-right px-1"
                          data-testid={`edit-qty-${i}`}
                        />
                      ) : (
                        it.quantity
                      )}
                    </td>
                    <td className="border border-black p-1 text-right align-top">
                      {editMode ? (
                        <Input
                          type="number"
                          step="0.01"
                          value={it.unit_price}
                          onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                          className="h-7 text-xs mono text-right px-1"
                          data-testid={`edit-rate-${i}`}
                        />
                      ) : (
                        Number(it.unit_price).toFixed(2)
                      )}
                    </td>
                    <td className="border border-black p-1 text-right align-top">{taxable.toFixed(2)}</td>
                    {intra ? (
                      <>
                        <td className="border border-black p-1 text-right">
                          {cgstAmt.toFixed(2)}
                          <div className="text-[8px] text-zinc-500">{gst / 2}%</div>
                        </td>
                        <td className="border border-black p-1 text-right">
                          {sgstAmt.toFixed(2)}
                          <div className="text-[8px] text-zinc-500">{gst / 2}%</div>
                        </td>
                      </>
                    ) : (
                      <td className="border border-black p-1 text-right">
                        {igstAmt.toFixed(2)}
                        <div className="text-[8px] text-zinc-500">{gst}%</div>
                      </td>
                    )}
                    <td className="border border-black p-1 text-right font-semibold">{total.toFixed(2)}</td>
                    {editMode && (
                      <td className="border border-black p-1 text-center">
                        <button
                          className="text-zinc-400 hover:text-red-600"
                          onClick={() => removeItem(i)}
                          title="Remove this line item"
                          data-testid={`edit-remove-${i}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {editMode && (
            <div className="flex justify-start mt-2 mb-3">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px] border-dashed"
                onClick={addItem}
                data-testid="preview-add-row"
              >
                <Plus size={12} className="mr-1" /> Add Row
              </Button>
            </div>
          )}

          {/* Totals + amount in words */}
          <div className="grid grid-cols-2 border border-t-0 border-black">
            <div className="p-3 border-r border-black">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
                Amount in Words
              </div>
              <div className="font-semibold" data-testid="invoice-amount-words">
                {editMode ? "(recomputes on save)" : inv.amount_in_words}
              </div>
              {inv.notes && !editMode && (
                <div className="mt-3 text-[10px] whitespace-pre-line">
                  <span className="text-zinc-500 uppercase text-[9px]">Notes:</span> {inv.notes}
                </div>
              )}
            </div>
            <div className="text-[10px] mono tabular">
              <TotalRow label="Subtotal (Taxable)" value={editMode ? previewSubtotal : inv.subtotal} />
              {intra ? (
                <>
                  <TotalRow label="CGST Total" value={editMode ? (previewSubtotal * (items[0]?.gst_rate || 0)) / 200 : inv.cgst_total} />
                  <TotalRow label="SGST Total" value={editMode ? (previewSubtotal * (items[0]?.gst_rate || 0)) / 200 : inv.sgst_total} />
                </>
              ) : (
                <TotalRow label="IGST Total" value={editMode ? items.reduce((s, i) => s + ((Number(i.quantity) || 0) * (Number(i.unit_price) || 0) * (Number(i.gst_rate) || 0)) / 100, 0) : inv.igst_total} />
              )}
              {inv.freight_charges > 0 && <TotalRow label="Freight Charges" value={inv.freight_charges} />}
              {inv.packing_charges > 0 && <TotalRow label="Packing Charges" value={inv.packing_charges} />}
              <TotalRow label="Grand Total (INR)" value={editMode ? (items.reduce((s, i) => {
                const q = Number(i.quantity) || 0, r = Number(i.unit_price) || 0, g = Number(i.gst_rate) || 0;
                return s + q * r * (1 + g / 100);
              }, 0) + (Number(inv.freight_charges) || 0) + (Number(inv.packing_charges) || 0)) : inv.grand_total} bold />
            </div>
          </div>

          <div className="grid grid-cols-2 mt-6 text-[10px]">
            <div>
              <div className="italic text-zinc-500">Computer Generated Invoice - No Signature Required.</div>
            </div>
            <div className="text-right">
              <div className="font-semibold">For {inv.seller.name}</div>
              <div className="h-12"></div>
              <div className="border-t border-black inline-block pt-1 px-6">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div className={`flex justify-between px-3 py-1 border-b border-zinc-300 last:border-b-0 ${bold ? "bg-zinc-100 font-bold" : ""}`}>
      <span className="font-sans text-zinc-600">{label}</span>
      <span>₹ {Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    </div>
  );
}
